/**
 * Frota · Manutenção · Nova manutenção — o registro avulso aberto pelos cartões da
 * Central (Eixos e rodas, Óleos e filtros, Demais serviços).
 *
 * Por baixo é uma O.S. corretiva com UM item de execução (ver quick-maintenance.ts).
 * A O.S. nasce ao informar a placa, para o painel ter onde gravar; o endereço guarda
 * `os` e `item`, então recarregar a página retoma de onde estava. "Cancelar" descarta
 * a O.S. se ninguém preencheu nada; "Salvar manutenção" conclui com o laudo gerado.
 */
import { BackButton } from "../components/BackButton";
import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, ApiError } from "../lib/api";
import { useAuth } from "../lib/auth";
import { AxleExecution, TilesExecution } from "../features/maintenance/ExecutionScreens";

type Tipo = "axles" | "oils" | "others";

const TIPOS: Record<Tipo, { titulo: string; abertura: string; avulso: string }> = {
  axles: { titulo: "Eixos e rodas", abertura: "Rolamentos, cubos, graxa e retentores.", avulso: "Nova manutenção" },
  oils: { titulo: "Óleos e filtros", abertura: "Motor, diferencial, câmbio e ARLA.", avulso: "Troca de óleos e filtros" },
  others: { titulo: "Demais serviços", abertura: "Freios, suspensão, elétrica e arrefecimento.", avulso: "Demais serviços" },
};

const ehTipo = (v: string | null): v is Tipo => v === "axles" || v === "oils" || v === "others";

export function MaintenanceQuickPage() {
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { user } = useAuth();
  const tipoParam = params.get("tipo");
  const tipo = ehTipo(tipoParam) ? tipoParam : null;
  const os = params.get("os");
  const item = params.get("item");

  const [placa, setPlaca] = useState((params.get("placa") ?? "").toUpperCase());
  const [erro, setErro] = useState<string | null>(null);
  const falha = (e: unknown, padrao: string) => setErro(e instanceof ApiError ? e.message : padrao);

  const sugestoes = useQuery({
    queryKey: ["maintenance", "vehicles", placa],
    queryFn: () => api.get<{ items: Array<{ plate: string; vehicleTypeName: string | null }> }>(`/maintenance/vehicles?q=${encodeURIComponent(placa)}`),
    enabled: placa.length >= 2 && !os,
  });

  const abrir = useMutation({
    mutationFn: () => api.post<{ workOrderId: string; itemId: string }>("/maintenance/quick-maintenance", { vehiclePlate: placa, kind: tipo }),
    onSuccess: (r) => {
      setErro(null);
      setParams({ tipo: tipo!, placa, os: r.workOrderId, item: r.itemId }, { replace: true });
    },
    onError: (e) => falha(e, "Não foi possível abrir a manutenção."),
  });

  // Vindo da consulta por placa: a placa já é conhecida, abre direto na tela de execução.
  const auto = useRef(false);
  useEffect(() => {
    if (tipo && !os && params.get("placa") && placa.length >= 5 && !auto.current) {
      auto.current = true;
      abrir.mutate();
    }
  }, [tipo, os]); // eslint-disable-line react-hooks/exhaustive-deps

  const salvar = useMutation({
    mutationFn: () => api.post(`/maintenance/quick-maintenance/${os}/finish`, {}),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["maintenance"] });
      navigate(`/frota/manutencao/ordens/${os}`);
    },
    onError: (e) => falha(e, "Não foi possível salvar a manutenção."),
  });

  const cancelar = async () => {
    if (os) await api.del(`/maintenance/quick-maintenance/${os}`).catch(() => undefined);
    qc.invalidateQueries({ queryKey: ["maintenance"] });
    navigate("/frota/manutencao");
  };

  if (!tipo) {
    return (
      <div className="card p-6 text-sm text-gray-600">
        Escolha o tipo de manutenção na <Link className="text-tm-700 underline" to="/frota/manutencao">Central</Link>.
      </div>
    );
  }
  const def = TIPOS[tipo];

  const Trilha = (
    <nav className="text-sm text-gray-500 [[data-layout=mobile]_&]:hidden" aria-label="Você está em">
      <Link to="/frota/manutencao" className="hover:underline">Manutenções</Link>
      <span className="mx-1.5">›</span>
      <span className="text-gray-700">{os ? def.avulso : def.titulo}</span>
    </nav>
  );

  if (os && item) {
    const props = {
      workOrderId: os, itemId: item, podeEditar: true, responsavel: user?.name ?? null,
      onFechar: cancelar, onMudou: () => qc.invalidateQueries({ queryKey: ["maintenance", "work-orders"] }),
      avulsa: { titulo: def.avulso, salvando: salvar.isPending, onSalvar: () => salvar.mutate() },
    };
    return (
      <div className="space-y-4">
        <BackButton fallback="/frota/manutencao/registrar" onClick={cancelar} />
        {Trilha}
        {erro && <p className="rounded-lg bg-danger-50 px-3 py-2 text-sm text-danger-700">{erro}</p>}
        {tipo === "axles" ? <AxleExecution {...props} /> : <TilesExecution {...props} />}
      </div>
    );
  }

  const pode = placa.length >= 5 && !abrir.isPending;
  return (
    <div className="space-y-4">
      <BackButton fallback="/frota/manutencao/registrar" />
      {Trilha}
      <div>
        <h2 className="text-3xl font-bold tracking-tight text-gray-900">{def.titulo}</h2>
        <p className="mt-1 text-gray-500">{def.abertura}</p>
      </div>
      <form
        className="card max-w-xl space-y-3 p-5"
        onSubmit={(e) => { e.preventDefault(); if (pode) abrir.mutate(); }}
      >
        <label className="block">
          <span className="mb-1 block text-sm font-medium text-gray-800">Placa do veículo *</span>
          <input
            className="input w-full font-mono uppercase" list="quick-placas" autoFocus autoComplete="off"
            value={placa} placeholder="Ex.: ABC1D23"
            onChange={(e) => setPlaca(e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, ""))}
          />
          <datalist id="quick-placas">
            {(sugestoes.data?.items ?? []).map((v) => <option key={v.plate} value={v.plate}>{v.vehicleTypeName ?? ""}</option>)}
          </datalist>
        </label>
        {erro && <p className="text-sm text-danger-600">{erro}</p>}
        <div className="flex justify-end gap-2">
          <Link to="/frota/manutencao" className="btn-secondary">Cancelar</Link>
          <button type="submit" className="btn-primary" disabled={!pode}>{abrir.isPending ? "Abrindo…" : "Continuar"}</button>
        </div>
      </form>
    </div>
  );
}
